import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelAccountDeletion,
  logoutUser,
  requestAccountDeletion,
  updateAccountSettings,
  updateEmail,
  updatePassword,
  updateProfile,
} from "../../../store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useTranslation } from "../../../translations/i18n";
import { RouteNames } from "../../../navigation/RouteNames";

export const useProfileScreen = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useTranslation();
  const { user, account, loading, error } = useAppSelector(
    (state) => state.auth,
  );

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [phone, setPhone] = useState(user?.phone || "");

  const [email, setEmail] = useState(user?.email || "");
  const [emailCurrentPassword, setEmailCurrentPassword] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");

  const [defaultCurrency, setDefaultCurrency] = useState(
    account?.default_currency || "BAM",
  );

  const [isAccountOpen, setAccountOpen] = useState(false);
  const [activeMenuSection, setActiveMenuSection] = useState<
    "pricing" | "privacy" | "support" | "settings" | null
  >(null);

  useEffect(() => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setPhone(user?.phone || "");
    setEmail(user?.email || "");
  }, [user]);

  useEffect(() => {
    setDefaultCurrency(account?.default_currency || "BAM");
  }, [account]);

  const handleSaveProfile = async () => {
    const result = await dispatch(
      updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim() || null,
        phone: phone.trim() || null,
      }),
    );

    if (updateProfile.fulfilled.match(result)) {
      window.alert(t("profile.profileUpdated"));
      return;
    }

    window.alert(String(result.payload || t("profile.failedProfileUpdate")));
  };

  const handleSaveEmail = async () => {
    const result = await dispatch(
      updateEmail({
        email: email.trim(),
        current_password: emailCurrentPassword,
      }),
    );

    if (updateEmail.fulfilled.match(result)) {
      setEmailCurrentPassword("");
      window.alert(t("profile.emailUpdated"));
      return;
    }

    window.alert(String(result.payload || t("profile.failedEmailUpdate")));
  };

  const handleSavePassword = async () => {
    if (newPassword !== newPasswordConfirmation) {
      window.alert(t("profile.passwordsDoNotMatch"));
      return;
    }

    const result = await dispatch(
      updatePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPasswordConfirmation,
      }),
    );

    if (updatePassword.fulfilled.match(result)) {
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirmation("");
      window.alert(t("profile.passwordUpdated"));
      return;
    }

    window.alert(String(result.payload || t("profile.failedPasswordUpdate")));
  };

  const handleSaveCurrency = async () => {
    const result = await dispatch(
      updateAccountSettings({
        default_currency: defaultCurrency,
      }),
    );

    if (updateAccountSettings.fulfilled.match(result)) {
      window.alert(t("currency.currencyUpdated"));
      return;
    }

    window.alert(String(result.payload || t("currency.failedCurrencyUpdate")));
  };

  const handleRequestAccountDeletion = async () => {
    const confirmation = window.prompt(t("accountDeletion.typeDelete"));

    if (confirmation?.trim().toUpperCase() !== "DELETE") {
      return;
    }

    const result = await dispatch(requestAccountDeletion());

    if (requestAccountDeletion.fulfilled.match(result)) {
      window.alert(t("accountDeletion.scheduledMessage"));
      return;
    }

    window.alert(String(result.payload || t("accountDeletion.failedRequest")));
  };

  const handleCancelAccountDeletion = async () => {
    if (!window.confirm(t("accountDeletion.cancelQuestion"))) {
      return;
    }

    const result = await dispatch(cancelAccountDeletion());

    if (cancelAccountDeletion.fulfilled.match(result)) {
      window.alert(t("accountDeletion.cancelledMessage"));
      return;
    }

    window.alert(String(result.payload || t("accountDeletion.failedCancel")));
  };

  const handleLanguageChange = (selectedLanguage: "bs" | "en") => {
    setLanguage(selectedLanguage);
  };

  const handleLogout = async () => {
    if (!window.confirm(t("profile.signOutQuestion"))) {
      return;
    }

    await dispatch(logoutUser());
    navigate(RouteNames.login, { replace: true });
  };

  return {
    t,
    user,
    account,
    loading,
    error,

    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone,

    email,
    setEmail,
    emailCurrentPassword,
    setEmailCurrentPassword,

    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    newPasswordConfirmation,
    setNewPasswordConfirmation,

    language,
    handleLanguageChange,

    handleSaveProfile,
    handleSaveEmail,
    handleSavePassword,
    handleLogout,

    defaultCurrency,
    setDefaultCurrency,
    handleSaveCurrency,

    handleRequestAccountDeletion,
    handleCancelAccountDeletion,

    isAccountOpen,
    setAccountOpen,
    activeMenuSection,
    setActiveMenuSection,
  };
};
